import { LocationStrategy } from '@angular/common';
import { Component, effect, HostBinding, OnInit, inject } from '@angular/core';
import { TopicsService } from '@kildencore/services/data/topics.service';
import { KildenStateService } from '@kildencore/services/kilden-state.service';
import { MapService } from '@kildencore/services/map.service';
import { TopicConfig } from '@kildenshared/interfaces/topic-config';
import { CesiumTerrainProvider } from 'cesium';
import { containsXY as olExtentContainsXY, type Extent } from 'ol/extent';
import { transform as olProjTransform, transformExtent as olProjTransformExtent } from 'ol/proj';
import { pickBottomPoint, pickCenterPoint, rotateAroundAxis } from 'olcs/core';
import OLCesium from 'olcs/OLCesium';
import {
  combineLatest,
  map as rxMap,
  Observable,
  shareReplay,
  Subject,
  take,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import ImageWMS from 'ol/source/ImageWMS';
import TileWMS from 'ol/source/TileWMS';
import { ThemeLayersService } from '@kildencore/services/theme-layers.service';
import { AppConfig } from '@kildenconfig/app.config';
import { PermaLinkService } from '@kildencore/services/perma-link.service';

/**
 * 3D-komponent som kobler sammen OpenLayers (2D) og Cesium (3D) via ol-cesium (olcs).
 *
 * Bakgrunn for temalag-håndteringen:
 * ol-cesium synkroniserer bakgrunnskart (WMTS) og vektorlag automatisk til Cesium, men IKKE
 * WMS-temalag (TileWMS). Disse må vi legge til manuelt som WebMapServiceImageryProvider i
 * Cesium sin imageryLayers-stack, og holde styr på dem i _cesiumImageryLayers slik at vi kan
 * legge til / fjerne / re-sortere etter hvert som brukeren endrer temalag i 2D-UI-et.
 *
 * Lagrekkefølge i Cesium imageryLayers (nederst -> øverst):
 *   [0]            bakgrunnskart (håndteres av ol-cesium)
 *   [..]           NIB / satellitt (flybilder) - nest nederst, over bakgrunn
 *   [..]           øvrige temalag - øverst
 */
@Component({
  selector: 'kilden3-three-d',
  templateUrl: './three-d.component.html',
  styleUrls: ['./three-d.component.css'],
  imports: [MatIcon, MatIconButton, MatProgressSpinner],
})
export class ThreeDComponent implements OnInit {
  private readonly _kildenStateService = inject(KildenStateService);
  private readonly _locationStrategy = inject(LocationStrategy);
  private readonly _mapService = inject(MapService);
  private readonly _topicsService = inject(TopicsService);
  private readonly _themeLayersService = inject(ThemeLayersService);
  private readonly _permaLinkService = inject(PermaLinkService);

  $is3dActive = this._mapService.$is3dActive;

  @HostBinding('class.isActive3d')
  get is3dActive() {
    return this.$is3dActive();
  }

  readonly dismissPopoverAfterSeconds = 30;
  dismissPopoverPercentageRemaining = 0;
  dismissPopoverTimer$: Observable<number> = timer(0, 250).pipe(
    takeWhile(() => this.dismissPopoverPercentageRemaining > 0),
    take(1 + (this.dismissPopoverAfterSeconds * 1000) / 250),
    tap((iterationsCount: number) => {
      this.dismissPopoverPercentageRemaining = Math.ceil(
        100 - ((iterationsCount * 0.25) / this.dismissPopoverAfterSeconds) * 100
      );
      if (this.dismissPopoverPercentageRemaining <= 0) {
        this.showIntroPopover = false;
      }
    })
  );

  // ol3d og _cesiumScene er utypet fordi olcs/Cesium-typene ikke spiller godt sammen her.
  ol3d: any;
  showIntroPopover: boolean = false;

  private readonly _onDestroy$ = new Subject<void>();

  /** Sporer hvilke Cesium-imagery-lag vi selv har lagt til, nøklet på temalagets id. */
  private readonly _cesiumImageryLayers = new Map<string, any>();

  /** Flybilder/satellitt som alltid skal ligge nest nederst (over bakgrunn, under øvrige temalag). */
  private readonly _NIB_IDS = new Set([AppConfig.IDBOD_NIB_2, AppConfig.IDBOD_NIB_SATELLITE]);

  private _cesiumScene: any;
  private _signalHasEmitted = false;
  private _on3dDeactivatedCallback: (() => void) | undefined;
  private _webGlRecoveryWired = false;
  private _lastImagerySortAt = 0;

  private _requestCesiumRender(): void {
    this._cesiumScene?.requestRender();
  }

  private _wireWebGlContextRecovery(): void {
    if (this._webGlRecoveryWired || !this._cesiumScene?.canvas) {
      return;
    }

    const canvas = this._cesiumScene.canvas as HTMLCanvasElement;
    canvas.addEventListener('webglcontextlost', (event: Event) => {
      // Allow context restoration instead of ending up with a permanently black canvas.
      event.preventDefault();
    });

    canvas.addEventListener('webglcontextrestored', () => {
      if (!this.$is3dActive()) {
        return;
      }

      this._syncThemeLayersToCesium();
      this._requestCesiumRender();
    });

    this._webGlRecoveryWired = true;
  }

  constructor() {
    // Reager på endringer i 3D-aktiv-signalet. Hopp over første emisjon (initialverdien)
    // slik at vi ikke trigger en overgang før brukeren faktisk slår 3D av/på.
    effect(() => {
      this.$is3dActive(); // les signalet for avhengighetssporing
      if (this._signalHasEmitted) {
        this.smooth3dTransition();
      }
      this._signalHasEmitted = true;
    });
  }

  ngOnInit(): void {
    /**
     * FYI: This component works with Cesium v1.105.2 and olcs v2.14.0. Upgrading these two libs
     * has been attempted but does not work yet (last tried June 2026, with cesium 1.142.0 + olcs 2.23.0).
     *
     * Root cause found during that attempt: olcs' FeatureConverter creates a Cesium billboard from
     * OL point features (the draw layer). In OL 10.x, imageStyle.getImage() returns an HTMLCanvasElement,
     * not an HTMLImageElement. olcs only awaits the image 'load' event when (image instanceof Image),
     * so for a canvas it calls billboards.add() immediately - before the canvas has a valid width.
     * Cesium 1.142 is stricter than 1.105 and throws "Cannot read properties of undefined (reading 'width')"
     * in loadImage(). This fires whenever a point is synced to Cesium, which the VectorSynchronizer does
     * in the background even when 3D is not enabled, so it breaks 2D point drawing too.
     *
     * To upgrade in the future, the fix must ensure the draw-layer point icon is a fully loaded
     * HTMLImageElement (with width/height set) before olcs syncs it - or olcs must be patched/updated to
     * handle the canvas case. Until olcs documents how to reliably tie together OL 10.x and Cesium,
     * keep these versions pinned.
     *
     * Idea: Refactor to delay loading Cesium until 3D active. This component is always included (to show the
     * 3D-toggle button), thus its ngOnInit() should probably not load Cesium at all. That load/activation and
     * deactivation should rather be handled by the toggle3d()-method.
     */
    this._initCesium();
    this._registerTopicListener();
    this._registerThemeLayerListeners();
    this._registerImageryStackKeepOnTop();

    this._mapService.registerThreeDComponent(this);
  }

  // ---------------------------------------------------------------------------
  // Initialisering
  // ---------------------------------------------------------------------------

  private _initCesium(): void {
    const map = this._mapService.getMap();
    map.getLayers().on(['add', 'remove'], () => {
      if (!this.$is3dActive()) {
        return;
      }

      setTimeout(() => {
        this._syncThemeLayersToCesium();
      });
    });
    Cesium.buildModuleUrl.setBaseUrl(`${this._locationStrategy.getBaseHref()}assets/cesium/`);

    // Fikser: jordsmonn --> 3d --> sonegrenser --> tilbake til jordsmonn
    const ds = map.getOverlayById('driftssenter');
    if (ds) map.removeOverlay(ds);

    // Fast tidspunkt for best mulig lyssetting (lyseste dag).
    const jDate = Cesium.JulianDate.fromDate(new Date(2020, 5, 21, 8, 30)); // 21. juni 2020, 08:30 UTC
    const time = () => jDate;

    this.ol3d = new OLCesium({ map, time });
    this._mapService.set3d(this.ol3d);
    this._mapService.saveProjOn3dInit();
    this._cesiumScene = this.ol3d.getCesiumScene();
    this._wireWebGlContextRecovery();

    // Egen Cesium Ion-konto/token. Gamle tokens er fjernet bevisst – ikke gjenbruk dem.
    Cesium.Ion.defaultAccessToken =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIxZWU3ZTI3Zi04MzliLTRjNWItYTJhZS1mOGNjMWU5MWRiMTMiLCJpZCI6MzEzNjU5LCJpYXQiOjE3NTAzMTU4MDl9.hwOxt823AicVzg1rifWzFmurPuEa1Yr7_Wxqz9j7y3k';

    Cesium.createWorldTerrainAsync({ requestVertexNormals: true }).then((terrainProvider: CesiumTerrainProvider) => {
      this._cesiumScene.terrainProvider = terrainProvider;
    });

    this._cesiumScene.camera.changed.addEventListener(() => {
      this.updateCompassRotation(this._cesiumScene.camera.heading);
    });

    // Skjul Cesium-credits.
    const creditContainer = document.querySelector('.cesium-credit-logoContainer')?.parentElement;
    if (creditContainer) creditContainer.style.display = 'none';

    this._cesiumScene.screenSpaceCameraController.minimumZoomDistance = 150;
    this._cesiumScene.screenSpaceCameraController.maximumZoomDistance = 4000000;
    // Render kun ved behov (ikke 60 FPS konstant).
    this._cesiumScene.requestRenderMode = true;
    // Hold tile-cache moderat for å unngå minneproblemer.
    this._cesiumScene.globe.tileCacheSize = 50;

    this._cesiumScene.postRender.addEventListener(this._limitCameraToNorway(map));
    this.ol3d.enableAutoRenderLoop();
  }

  /**
   * Holder kameraet innenfor et maksimalt utsnitt (Norge + omland). Returnerer en funksjon
   * som kjøres på hver postRender.
   */
  private _limitCameraToNorway(map: ReturnType<MapService['getMap']>): () => void {
    const extent = [4.1, 57.9, 31.7, 72]; // maks utsnitt i grader
    return () => {
      const v = map.getView();
      const epsg = v.getProjection().getCode();
      const c = olProjTransform(v.getCenter() || [0, 0], epsg, 'EPSG:4258');
      const cp = { lon: c[0], lat: c[1] }; // fallback hvis pickCenterPoint feiler

      const cpCartesian = pickCenterPoint(this._cesiumScene);
      if (cpCartesian) {
        const cpRadians = Cesium.Cartographic.fromCartesian(cpCartesian);
        cp.lon = (cpRadians.longitude * 180) / Math.PI;
        cp.lat = (cpRadians.latitude * 180) / Math.PI;
      }

      if (olExtentContainsXY(extent, cp.lon, cp.lat)) return;

      const camera = this._cesiumScene.camera;
      const outLon = cp.lon;
      const outLat = cp.lat;
      cp.lon = Math.min(Math.max(extent[0], cp.lon), extent[2]);
      cp.lat = Math.min(Math.max(extent[1], cp.lat), extent[3]);
      const deltaLon = ((outLon - cp.lon) * Math.PI) / 180;
      const deltaLat = ((outLat - cp.lat) * Math.PI) / 180;
      const cpc = camera.positionCartographic;
      // prettier-ignore
      camera.setView({
        destination: Cesium.Cartesian3.fromRadians(
          cpc.longitude - deltaLon, cpc.latitude - deltaLat, cpc.height
        ),
        orientation: { heading: camera.heading, pitch: camera.pitch },
      });
    };
  }

  // ---------------------------------------------------------------------------
  // Lyttere
  // ---------------------------------------------------------------------------

  /** Deaktiver 3D automatisk hvis nytt fagområde (topic) ikke har 3D i verktøylisten. */
  private _registerTopicListener(): void {
    combineLatest({
      id: this._kildenStateService.topic$,
      configs: this._topicsService.topicsConfig$.pipe(shareReplay(1)),
    })
      .pipe(
        rxMap(combined => combined.configs.find(cfg => cfg.id === combined.id) as TopicConfig),
        takeUntil(this._onDestroy$)
      )
      .subscribe((topicConfig: TopicConfig) => {
        if (this.$is3dActive() && !topicConfig.toolList.includes('3D')) {
          this.toggle3dActiveSignal();
        }
      });
  }

  /**
   * Alle hendelser som påvirker temalagene i 3D. Hver lytter er en no-op når 3D ikke er aktiv,
   * siden OpenLayers da håndterer temalagene selv.
   */
  private _registerThemeLayerListeners(): void {
    // Bakgrunnskartbytte: ol-cesium legger til nytt bakgrunnslag, så vi re-synker temalagene
    // slik at de fortsatt ligger øverst. setTimeout lar ol-cesium legge til sitt lag først.
    this._mapService.bgLayerChanged$.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      if (!this.$is3dActive()) return;
      setTimeout(() => {
        this._syncThemeLayersToCesium();
        this._requestCesiumRender();
      }, 100);
    });

    // Rekkefølgeendring via drag&drop i "Aktive kartlag". Dette trigger ikke layersChange$,
    // derfor egen kanal. Vi fjerner og re-oppretter alle lag for å få ny rekkefølge.
    this._themeLayersService.layersReorder$.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      if (!this.$is3dActive()) return;
      setTimeout(() => {
        this._reorderCesiumLayers();
        this._requestCesiumRender();
      }, 100);
    });

    // Opacity-endring i sanntid (slider).
    this._themeLayersService.layerOpacityChange$.pipe(takeUntil(this._onDestroy$)).subscribe(({ id, opacity }) => {
      if (!this.$is3dActive()) return;
      const cesiumLayer = this._cesiumImageryLayers.get(id);
      if (cesiumLayer) {
        cesiumLayer.alpha = opacity;
        this._requestCesiumRender();
      }
    });

    // Re-synk temalag til Cesium når lag endres
    this._themeLayersService.layersChange$.pipe(takeUntil(this._onDestroy$)).subscribe(changes => {
      if (!this.$is3dActive()) return;

      // Fjern og re-opprett Cesium-lag ved variantbytte
      changes
        .filter(lc => lc.change.selectedVariant !== undefined)
        .forEach(lc => {
          const oldCesiumLayer = this._cesiumImageryLayers.get(lc.layerid);
          this._cesiumImageryLayers.delete(lc.layerid);
          if (oldCesiumLayer) {
            this._cesiumScene.globe.imageryLayers.remove(oldCesiumLayer);
          }
        });

      this._syncThemeLayersToCesium();
      this._requestCesiumRender();
    });
    this._mapService.registerThreeDComponent(this);
  }

  /**
   * Holder temalagene på rett plass i imagery-stacken på hver render.
   *
   * Nødvendig fordi enkelte interaksjoner (f.eks. objektinformasjon-klikk) får ol-cesium til å
   * sette inn sin egen OLImageryProvider mellom bakgrunn og temalag, slik at temalagene ellers
   * skjules. Vi kjører hele sorteringen (ikke bare raiseToTop) slik at både NIB/flybilder
   * (nest nederst) og øvrige temalag (øverst) holdes riktig – ellers ville flybilder bli skjult
   * av OLImageryProvider ved objektinfo. Lytteren er en no-op når 3D er av eller ingen temalag finnes.
   */
  private _registerImageryStackKeepOnTop(): void {
    this._cesiumScene.postRender.addEventListener(() => {
      if (!this.$is3dActive() || this._cesiumImageryLayers.size === 0) return;

      const now = Date.now();
      if (now - this._lastImagerySortAt < 250) return;
      this._lastImagerySortAt = now;

      const activeLayers = this._themeLayersService.getActiveThemeLayers();
      this._sortCesiumThemeLayers(activeLayers);
    });
  }

  // ---------------------------------------------------------------------------
  // 3D-overgang og av/på
  // ---------------------------------------------------------------------------

  flyCountryExtent = () => {
    this._cesiumScene.camera.flyTo({
      destination: Cesium.Rectangle.fromDegrees(-7, 56, 30, 74),
    });
  };

  /**
   * Deaktiver 3D og kjør en callback når overgangen til 2D er ferdig (etter flyTo).
   * Brukes f.eks. ved fagområdebytte som krever at 2D er på plass først.
   */
  deactivate3dWithCallback(callback: () => void): void {
    this._on3dDeactivatedCallback = callback;
    this._mapService.$is3dActive.set(false);
  }

  smooth3dTransition = () => {
    const activating3d = this.$is3dActive();

    if (activating3d) {
      // "Ingen bakgrunn" støttes ikke i 3D (bryter ol-cesium sin bakgrunnssynkronisering),
      // så bytt til gråtoner før vi går inn i 3D.
      const currentBgId = this._mapService.getCurrentBackgroundLayer().get('id');
      if (currentBgId === 'ingen') {
        this._mapService.changeBackgroundLayer('graatone');
      }
      this.showIntroPopover = true;
      this.dismissPopoverPercentageRemaining = 100;
      this.dismissPopoverTimer$.subscribe();
      this.adapt3dExtent();
      this.initialTilt(Math.PI / 4);
      this.toggle3d(activating3d);
    } else {
      this.showIntroPopover = false;
      const cp = pickBottomPoint(this._cesiumScene);
      if (cp) {
        cp.z = this._cesiumScene.camera.position.z * 1.001;
      }
      const target = cp ? cp : this._cesiumScene.camera.position;

      this._mapService.suspendUrlUpdate(true);
      this._cesiumScene.camera.flyTo({
        destination: target,
        complete: () => {
          this._mapService.suspendUrlUpdate(false);
          this.toggle3d(activating3d);
          this.adapt2dExtent();

          // Tving overlays til å re-rendre i 2D.
          setTimeout(() => {
            const map = this._mapService.getMap();
            map.getOverlays().forEach(o => {
              map.removeOverlay(o);
              map.addOverlay(o);
            });
          }, 200);

          // Kall callback når 3D er ferdig deaktivert (engangs).
          this._on3dDeactivatedCallback?.();
          this._on3dDeactivatedCallback = undefined;
        },
      });
      this._mapService.getMap().getView().animate({ rotation: 0 });
    }
  };

  toggle3dActiveSignal(): void {
    this._mapService.$is3dActive.set(!this._mapService.$is3dActive());
  }

  /**
   * Skrur ol-cesium av/på og bytter projeksjon. Ved aktivering synker vi temalagene til Cesium
   * etter første render; ved deaktivering fjerner vi alle våre Cesium-temalag.
   */
  private toggle3d = (activating3d: boolean) => {
    if (!this.ol3d) return;
    this.ol3d.setEnabled(activating3d);

    const olcsContainerDiv = document.querySelector('.ol-overlaycontainer-stopevent') as HTMLElement;
    if (olcsContainerDiv) olcsContainerDiv.style.zIndex = '1';

    // I 3D bruker vi alltid EPSG:3857; tilbake til UTM-projeksjonen som var aktiv før 3D.
    const epsg2d = this._mapService.projOn3dInit;
    const oldEpsg = activating3d ? epsg2d : 'EPSG:3857';
    const nextEpsg = activating3d ? 'EPSG:3857' : epsg2d;
    this._mapService.changeLayersProjection(oldEpsg, nextEpsg, activating3d ? +1 : -1);

    if (activating3d) {
      // On repeated 2D->3D toggles Cesium can stay idle unless we trigger a fresh render cycle.
      window.dispatchEvent(new Event('resize'));
      this._requestCesiumRender();

      // Vent til Cesium har rendret bakgrunnskartet før vi legger temalagene oppå.
      const removeCallback = this._cesiumScene.postRender.addEventListener(() => {
        removeCallback();
        this._syncThemeLayersToCesium();
        this._requestCesiumRender();
      });

      // Fallback if postRender callback doesn't fire quickly enough.
      setTimeout(() => {
        if (!this.$is3dActive()) return;
        this._syncThemeLayersToCesium();
        this._requestCesiumRender();
      }, 200);
    } else {
      this._cesiumImageryLayers.forEach(cesiumLayer => {
        this._cesiumScene.globe.imageryLayers.remove(cesiumLayer);
      });
      this._cesiumImageryLayers.clear();
      this._requestCesiumRender();
    }
  };

  // ---------------------------------------------------------------------------
  // Cesium imagery-synkronisering
  // ---------------------------------------------------------------------------

  /** Fjern ett sporet Cesium-temalag (både fra stacken og fra _cesiumImageryLayers). */
  private _removeCesiumThemeLayer(id: string): void {
    const cesiumLayer = this._cesiumImageryLayers.get(id);
    if (cesiumLayer) {
      this._cesiumScene.globe.imageryLayers.remove(cesiumLayer);
    }
    this._cesiumImageryLayers.delete(id);
  }

  /** Fjern alle sporede Cesium-temalag. */
  private _removeAllCesiumThemeLayers(): void {
    const imageryLayers = this._cesiumScene.globe.imageryLayers;
    this._cesiumImageryLayers.forEach(cesiumLayer => imageryLayers.remove(cesiumLayer));
    this._cesiumImageryLayers.clear();
  }

  /** Fjern alt og bygg opp på nytt – brukes når rekkefølgen er endret. */
  private _reorderCesiumLayers(): void {
    this._removeAllCesiumThemeLayers();
    this._syncThemeLayersToCesium();
    this._requestCesiumRender();
  }

  /**
   * Synkroniserer aktive WMS-temalag fra OpenLayers til Cesium sin imagery-stack.
   * Idempotent: legger kun til lag som mangler, fjerner lag som ikke lenger er aktive,
   * og sorterer til slutt (NIB nest nederst, øvrige øverst).
   */
  private _syncThemeLayersToCesium(): void {
    const imageryLayers = this._cesiumScene.globe.imageryLayers;
    const activeLayers = this._themeLayersService.getActiveThemeLayers();
    const activeIds = new Set(activeLayers.map(l => l.get('id')));

    // Fjern lag som ikke lenger er aktive.
    this._cesiumImageryLayers.forEach((_cesiumLayer, id) => {
      if (!activeIds.has(id)) this._removeCesiumThemeLayer(id);
    });

    // Legg til nye aktive lag.
    activeLayers.forEach(layer => {
      if (!(layer instanceof TileLayer || layer instanceof ImageLayer)) return;
      const id = layer.get('id');
      if (this._cesiumImageryLayers.has(id)) return;
      const source = layer.getSource() as TileWMS | ImageWMS;
      if (!source) return;

      const cesiumLayer = imageryLayers.addImageryProvider(
        new Cesium.WebMapServiceImageryProvider({
          url: source instanceof ImageWMS ? (source.getUrl() ?? '') : (source.getUrls()?.[0] ?? ''),
          layers: source.getParams()['LAYERS'],
          parameters: {
            FORMAT: source.getParams()['FORMAT'] || 'image/png',
            TRANSPARENT: true,
            VERSION: source.getParams()['VERSION'] || '1.3.0',
            ...(source.getParams()['token'] ? { token: source.getParams()['token'] } : {}),
          },
          tilingScheme: new Cesium.WebMercatorTilingScheme(),
          crs: 'EPSG:3857',
          maximumLevel: 18,
          minimumLevel: 0,
          tileWidth: 512,
          tileHeight: 512,
        })
      );

      // NIB/satellitt skal alltid være ugjennomsiktige; øvrige henter opacity fra permalink/lag.
      const paramLayer = this._permaLinkService.getParamLayerById(id);
      cesiumLayer.alpha = this._NIB_IDS.has(id) ? 1.0 : (paramLayer?.opacity ?? layer.getOpacity());
      this._cesiumImageryLayers.set(id, cesiumLayer);
    });

    this._sortCesiumThemeLayers(activeLayers);
    this._requestCesiumRender();
  }

  /**
   * Sorterer Cesium-temalagene: NIB/satellitt legges nest nederst (rett over bakgrunnen),
   * øvrige temalag øverst. nonThemeCount er antall lag som ikke er våre temalag (bakgrunn +
   * evt. ol-cesium-interne lag) – NIB heves akkurat så mange hakk at det havner like over dem.
   */
  private _sortCesiumThemeLayers(activeLayers: ReturnType<ThemeLayersService['getActiveThemeLayers']>): void {
    const imageryLayers = this._cesiumScene.globe.imageryLayers;
    const nonThemeCount = imageryLayers.length - this._cesiumImageryLayers.size;

    // Iterer baklengs slik at det første laget i activeLayers (øverst i UI) heves sist
    // og dermed havner øverst i Cesium-stacken.
    [...activeLayers].reverse().forEach(layer => {
      const id = layer.get('id');
      const cesiumLayer = this._cesiumImageryLayers.get(id);
      if (!cesiumLayer) return;

      if (this._NIB_IDS.has(id)) {
        imageryLayers.lowerToBottom(cesiumLayer);
        for (let i = 0; i < nonThemeCount; i++) {
          imageryLayers.raise(cesiumLayer);
        }
      } else {
        imageryLayers.raiseToTop(cesiumLayer);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Kamera / utsnitt
  // ---------------------------------------------------------------------------

  private adapt2dExtent = () => {
    if (!this.ol3d || !this._cesiumScene) return;
    const epsg2d = this._mapService.getMap().getView().getProjection().getCode();
    const e3d = this._cesiumScene.camera.computeViewRectangle();
    const west = Cesium.Math.toDegrees(e3d.west);
    const south = Cesium.Math.toDegrees(e3d.south);
    const east = Cesium.Math.toDegrees(e3d.east);
    const north = Cesium.Math.toDegrees(e3d.north);
    const w = 0.1 * (east - west);
    const h = 0.1 * (north - south);
    const e = [west + w, south + h, east - w, north - h];
    const e2d = olProjTransformExtent(e, 'EPSG:4258', epsg2d);
    this._mapService.getMap().getView().fit(e2d);
  };

  private adapt3dExtent = () => {
    if (!this.ol3d || !this._cesiumScene) return;
    const epsg2d = this._mapService.getMap().getView().getProjection().getCode();
    const e2d = this._mapService.getMap().getView().calculateExtent();
    const e = olProjTransformExtent(e2d, epsg2d, 'EPSG:4258');
    const w = 0.1 * (e[2] - e[0]);
    const h = 0.1 * (e[3] - e[1]);
    // prettier-ignore
    this._cesiumScene.camera.setView({
      destination: Cesium.Rectangle.fromDegrees(
        e[0] + w, e[1] + h, e[2] - w, e[3] - h
      ),
    });
  };

  public zoomToUploadExtent = (extent: Extent, fromEpsg: string) => {
    if (!this.ol3d || !this._cesiumScene || !extent) return;

    const e2d = olProjTransformExtent(extent, fromEpsg, 'EPSG:4258');
    const w = Math.max(0.0001, 0.5 * (e2d[2] - e2d[0]));
    const h = Math.max(0.0001, 0.5 * (e2d[3] - e2d[1]));
    const west = e2d[0] - w;
    const south = e2d[1] - h;
    const east = e2d[2] + w;
    const north = e2d[3] + h;

    if (!(west < east && south < north)) {
      return;
    }

    this._cesiumScene.camera.setView({
      destination: Cesium.Rectangle.fromDegrees(west, south, east, north),
    });
    this._requestCesiumRender();
  };

  private initialTilt = (angle: number) => {
    if (!this.ol3d || !this._cesiumScene) return;
    const camera = this._cesiumScene.camera;
    const bottom = pickBottomPoint(this._cesiumScene);
    const transform = Cesium.Matrix4.fromTranslation(bottom);
    const axis = camera.right;
    this.watchDistance();
    rotateAroundAxis(camera, -angle, axis, transform);
  };

  private watchDistance = () => {
    // prettier-ignore
    const d = Cesium.Cartesian3.distance(
      this._cesiumScene.camera.position, pickCenterPoint(this._cesiumScene)
    );
    const maxD = 1.0 * Math.pow(10, 6);
    if (d > maxD) {
      this._cesiumScene.camera.moveDown(200000);
      this._cesiumScene.camera.moveForward(d - maxD);
    }
  };

  private previousHeading = 0;

  private updateCompassRotation(heading: number): void {
    const compassNeedle = document.querySelector('.compass') as HTMLElement;
    if (!compassNeedle) return;

    const rotationDegrees = Cesium.Math.toDegrees(heading);
    let rotationDiff = rotationDegrees - this.previousHeading;
    if (rotationDiff > 180) rotationDiff -= 360;
    else if (rotationDiff < -180) rotationDiff += 360;
    this.previousHeading += rotationDiff;
    compassNeedle.style.transition = 'transform 0.1s ease-in-out';
    compassNeedle.style.transform = `rotate(${this.previousHeading}deg)`;
  }
}
